/**
 * THE WIRE CONTRACT OF THE AGENT SURFACE — types and pure readers, no React,
 * no DOM, no network, and no import of anything under `@/`.
 *
 * ══ WHY THIS FILE IS DEPENDENCY-FREE ═══════════════════════════════════════
 *
 * Everything in here is exercised by `tests/unit/admin/*.test.ts`, which run
 * under the ROOT jest project (`testEnvironment: 'node'`, no jsdom, no
 * `@/` path mapping). A single `@/lib/utils` import here would make the whole
 * decision layer untestable, and an untestable decision layer is where a
 * fabricated zero hides. The React components import from here; nothing here
 * imports a component.
 *
 * ══ THE ONE RULE EVERY READER IN THIS FILE OBEYS ═══════════════════════════
 *
 * 🔴 A VALUE THE SYSTEM DECIDED NOT TO SERVE NEVER BECOMES A NUMBER. The
 * envelope's `Cell` is `number | string | null | { suppressed, floor }`, and
 * `Intl.NumberFormat().format(null)` is the string **"0"** — a fabricated zero
 * over a number the disclosure engine has just withheld. So `readCell` returns
 * a TAGGED union with separate `suppressed` and `gap` arms, and there is no
 * code path from either arm into a number formatter. Spec 4.5, 16.1, 16.2.
 *
 * ══ WHAT THE TRANSPORT ACTUALLY PUTS ON THE WIRE ═══════════════════════════
 *
 * `GET /v1/admin/agent/turn/{id}` answers the `STATUS` item plus, once
 * `status === 'done'`, the `RESULT` item. Each entry of `result.results[]` is
 * a `ToolResultRecord` whose `model_view` is the WRAPPER the tool handed the
 * model and whose optional `ui_view` is the full-precision twin (spec 14.2,
 * R9). The wrappers differ per tool and are read here, in one place:
 *
 *   metric_query      model_view: { model_view: Envelope }   ui_view: Envelope
 *                     …or, on a disclosure refusal: { refusal: {...} }
 *   metric_compare    model_view: { model_view: { window_a, window_b, delta } }
 *                     ui_view: { window_a, window_b }
 *   metric_describe   model_view: { model_view: {...} }      ui_view: {...}
 *   catalog_search    model_view: { model_view: Summary[] }
 *   source_health     model_view: { model_view: Entry[], summary: Counts }
 *                     (the screen renders the entries; `summary` is for the model)
 *   clarify_or_decline model_view: { model_view: { acknowledged: true } }
 *   explore           model_view: { model_view: Envelope, composed_from }   ui_view: Envelope
 *                     (2026-09-23: the same wrapper as metric_query, so it renders as a table;
 *                     its metric_id is explore:<relation> and its invocation carries the spec)
 *   explore_describe  model_view: { model_view: {...} }      ui_view: {...}
 *   database_query    model_view: { model_view: Envelope }   ui_view: { ...Envelope, sql, purpose }
 *                     …or, when the guard or the database refused: { model_view: null, error }
 *                     (row-level, owner decision 2026-10-15; metric_id is 'database_query')
 *   database_describe model_view: { model_view: View[] }     (how the agent found it)
 *
 * `readToolPresentation` returns a CLOSED union with an `unknown` arm, so a
 * tool shipped ahead of this file renders a labelled "not renderable here"
 * card instead of throwing inside a render pass.
 */

// ═══════════════════════════════════════════════════════════════════════════
// THE ENVELOPE, restated structurally
// ═══════════════════════════════════════════════════════════════════════════
//
// Restated rather than imported: the admin panel does not bundle the backend
// package, and the same convention is already used by `pages/growth/index.tsx`
// for `FlywheelMetrics`. The shapes are pinned to the backend by
// `tests/unit/lint/agent-screen-single-view.test.ts`.

export type SuppressedCell = { suppressed: true; floor: number };
export type Cell = number | string | null | SuppressedCell;
export type Row = Record<string, Cell>;

export type ColumnRole = 'time' | 'dimension' | 'measure' | 'derived';

export type ColumnFormat =
  | 'integer'
  | 'decimal'
  | 'percent'
  | 'currency_cents'
  | 'seconds'
  | 'text'
  | 'date';

export interface ColumnSpec {
  key: string;
  label: string;
  role: ColumnRole;
  grain: string | null;
  format: ColumnFormat;
}

export interface MetricResultEnvelope {
  metric_id: string;
  metric_version: string;
  population_id: string;
  population_predicate: string;
  columns: ColumnSpec[];
  rows: Row[];
  derived: Record<string, number>;
  as_of: string;
  freshness_ok: boolean;
  data_recency?: string;
  cells_suppressed: number;
  k_applied: number | null;
  tier: number;
  environment: 'dev' | 'predev' | 'prod';
  model_view_as_of: string;
  definition_url: string | null;
  unavailable?: { reason: string; source_key: string };
  /** The period asked starts before the day the source began recording
   *  (backend `RecordedSince`): an earlier period is unrecorded, not zero. */
  recorded_since?: {
    first_recorded_day: string | null;
    covers: 'nothing' | 'part';
    /** Per-network ad sources (migration 366): the last covered day, and the days of the period
     *  that no reading covered (after that day, or in a gap). Not recorded, never zero. */
    last_recorded_day?: string;
    not_recorded?: readonly { from: string; to: string }[];
  };
  /** `model_view` only: the request was below the processor's grain/window
   *  floor, so the model received nothing while the operator's view is full. */
  withheld_from_model?:
    | {
        reason: 'below_grain_floor' | 'below_window_floor';
        floor_grain: string;
        floor_window_days: number;
        served_to_operator: true;
      }
    | { reason: 'total_and_breakdown_within_24h'; already_served: string; served_to_operator: true }
    | { reason: 'cannot_be_replayed'; served_to_operator: true }
    | { reason: 'special_category_attribute'; param: string; served_to_operator: true }
    | { reason: 'population_below_floor'; floor: number; served_to_operator: true }
    | { reason: 'daily_budget_exhausted'; ceiling: number; served_to_operator: true };
}

// ═══════════════════════════════════════════════════════════════════════════
// THE TRANSPORT BODIES
// ═══════════════════════════════════════════════════════════════════════════

export type TurnStatus = 'queued' | 'running' | 'done' | 'failed';

export type TurnPhase =
  | 'queued'
  | 'gated'
  | 'thinking'
  | 'tool'
  | 'writing'
  | 'done'
  | 'failed';

export type TurnOutcome =
  | 'answered'
  | 'declined'
  | 'clarified'
  | 'refused'
  | 'error'
  | 'deadline_exceeded';

export interface TurnDecline {
  kind: 'clarify' | 'decline';
  reason_code: string;
  message: string;
  suggested_metric_ids?: string[];
}

export interface ToolResultRecord {
  tool: string;
  metric_id: string | null;
  metric_version: string | null;
  family: string | null;
  model_view: unknown;
  ui_view?: unknown;
  /** metric_query only: the exact question this table answers. */
  invocation?: QueryInvocation | null;
}

export interface QueryInvocation {
  params: Record<string, string | number | boolean | null>;
  dimensions: string[];
  grain: string;
  window: { from: string; to: string };
}

/** The `RESULT` item as the front door serialises it (`resultPayload`). */
/** Step 7b of the worker turn — the shadow semantic answer check (design record 2026-09-20). */
export interface AnswerCheckSummary {
  mode: 'shadow';
  status: 'ok' | 'skipped';
  reason?: string;
  model?: string;
  nouls?: {
    addresses_question: number;
    subject_and_window_match: number;
    claims_beyond_tools: number;
  };
  flags: Array<'low_address' | 'low_match' | 'overclaim'>;
  latency_ms?: number;
}

export interface TurnResultPayload {
  turn_id: string;
  session_id: string;
  outcome: TurnOutcome | string;
  prose: string;
  decline: TurnDecline | null;
  results: ToolResultRecord[];
  metric_ids: string[];
  families: string[];
  model_id: string;
  iterations: number;
  duration_ms: number;
  prose_fidelity_replaced: boolean;
  /** `null` when the check is off in this environment. Older items lack the key — treat as null. */
  answer_check?: AnswerCheckSummary | null;
  gate_allow: boolean;
  gate_reason: string | null;
  environment: string;
  egress_profile: string;
  completed_at: string;
}

/** The poll body (`statusPayload` + the four fields `handleGet` adds). */
export interface TurnPollResponse {
  turn_id: string;
  session_id: string;
  status: TurnStatus;
  phase: TurnPhase;
  iteration: number;
  tool_calls_done: number;
  tool: string | null;
  environment: string;
  egress_profile: string;
  deadline_at: string;
  created_at: string;
  updated_at: string;
  poll_after_ms: number | null;
  error: string | null;
  result: TurnResultPayload | null;
}

/** `POST /v1/admin/agent/turn` → 202. */
export interface TurnAcceptedResponse {
  turn_id: string;
  poll_after_ms: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// THE LIBRARY BODIES (spec 16.4)
// ═══════════════════════════════════════════════════════════════════════════

export type DateRangeSpec = (
  | { kind: 'absolute'; from: string; to: string }
  | { kind: 'trailing_days'; days: number }
) & { grain?: string };

export interface SavedQuestion {
  id: string;
  label: string;
  metric_id: string;
  params: Record<string, string | number | boolean> | null;
  dimensions: string[] | null;
  date_range_spec: DateRangeSpec | null;
  visibility: 'private' | 'team';
  is_owner: boolean;
  created_at: string;
}

export interface StarterQuestion {
  metric_id: string;
  title: string;
  question: string;
}

/**
 * WHAT AN ADMIN SEES INSTEAD OF A NUMBER THEY MAY NOT READ, or a number the
 * kill switch withheld. It carries NO numeric field of any kind, and that is
 * the point — mirrors `RedactedCell` in `backend/src/routes/admin-agent-library.ts`.
 */
export interface RedactedAnswerCell {
  redacted: true;
  reason: 'permission' | 'metric_unavailable' | 'metric_disarmed' | 'metric_disarm_unavailable';
  metric_id: string;
  missing_permission_key?: string;
  metric_title?: string | null;
}

/** One entry of the pinned board, and the body of the share page. */
export interface AnswerBody {
  pin_id: string;
  title: string;
  label?: string;
  shared?: boolean;
  metric_id?: string;
  cell: RedactedAnswerCell | null;
  rows: Row[] | null;
  /**
   * The MEASURE formats for `rows`, served alongside them.
   *
   * 🔴 WITHOUT THIS A MONEY FIGURE ARRIVES WITH NO UNIT. `revenue_cents` is
   * `currency_cents`; the chat surface has the specs and renders `$2,499.00`,
   * and this surface had none and rendered `249,900` — the same figure, 100x
   * apart, on the link one admin sends to another.
   *
   * Absent on a REDACTED body, which carries no rows to describe.
   */
  /** `label` since 2026-09-21: the metric's own heading for the column. */
  columns?: { key: string; format: ColumnFormat; label?: string }[];
  as_of: string | null;
  /**
   * The source's freshness verdict, as `runMetric` computed it.
   *
   * 🔴 WITHOUT IT A STALE NUMBER LOOKS CURRENT. The chat card marks a result
   * stale from this same field (`answer-view.ts`), and the pinned tile and the
   * share page served only `as_of` — a date beside a number reads as proof of
   * freshness, not as a warning. Absent on a REDACTED body, which has no rows.
   */
  freshness_ok?: boolean;
  deep_link: { href: string; label: string } | null;
}

export interface PinnedTileData {
  pins: AnswerBody[];
}

/**
 * A RE-OPENED SAVED QUESTION: today's run of its stored invocation
 * (`GET …/saved-question/{id}/run`, spec §16.4 primitive 1). The share page's
 * body, keyed by the saved question, plus the period the stored spec resolved
 * to today — "the last 7 days" re-run on another day is another period.
 * `window` and `grain` are null on a redacted body.
 */
export interface SavedRunBody extends Omit<AnswerBody, 'pin_id'> {
  saved_question_id: string;
  window: { from: string; to: string } | null;
  grain: string | null;
}

/** A dashboard tile the kill switch withheld (`withheld[]`, spec 8.4). */
export interface WithheldTile {
  tile: string;
  requires: string;
  reason: 'metric_disarmed' | 'metric_disarm_unavailable';
  metric_id: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// CELL READING — the only place a `Cell` is interpreted
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A cell, TAGGED.
 *
 * `suppressed` and `gap` are DIFFERENT FACTS and are rendered differently: one
 * says "we hold this number and chose not to serve it at this grain", the
 * other says "there is no number here". Collapsing them was the shortcut that
 * would have let a formatter see `null`.
 */
export type ReadCell =
  | { kind: 'number'; value: number }
  | { kind: 'text'; value: string }
  | { kind: 'suppressed'; floor: number }
  | { kind: 'gap' };

export function isSuppressedCell(cell: unknown): cell is SuppressedCell {
  return (
    typeof cell === 'object' &&
    cell !== null &&
    (cell as { suppressed?: unknown }).suppressed === true &&
    typeof (cell as { floor?: unknown }).floor === 'number'
  );
}

export function readCell(cell: Cell | undefined): ReadCell {
  if (isSuppressedCell(cell)) return { kind: 'suppressed', floor: cell.floor };
  if (typeof cell === 'number' && Number.isFinite(cell)) return { kind: 'number', value: cell };
  if (typeof cell === 'string') return { kind: 'text', value: cell };
  return { kind: 'gap' };
}

/**
 * 🔴 THE CHART TWIN OF THE EM-DASH RULE (spec 16.2).
 *
 * A suppressed cell maps to `null` — a GAP in the series — and NEVER to `0`.
 * Recharts draws a break in the line for `null`; a `0` would draw a plotted
 * point at the origin, which is a fabricated measurement of a number the
 * system just decided not to serve, and it is worse than a missing point
 * because a reader cannot tell it from a real zero.
 */
export function chartValue(cell: Cell | undefined): number | null {
  const read = readCell(cell);
  return read.kind === 'number' ? read.value : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOL PRESENTATION — one reader for six wrappers
// ═══════════════════════════════════════════════════════════════════════════

/** Which of the two views the screen is showing. C13: `model_view` by default. */
export type Precision = 'model' | 'ui';

export interface EnvelopeSlot {
  /** `null` for a single-envelope tool; `Window A` / `Window B` for a compare. */
  label: string | null;
  envelope: MetricResultEnvelope;
  /**
   * For `database_query` only: the statement the agent ran and why (owner decision
   * 2026-10-15 — the operator sees exactly what was read). Read from `ui_view` in
   * BOTH precisions: the SQL is the same whichever rows are on screen.
   */
  query?: SqlQueryInfo | null;
}

export interface SqlQueryInfo {
  sql: string;
  purpose: string | null;
}

/** The tools that read the row-level views (`agent_rows`, migration 369). */
const DATABASE_QUERY_TOOL = 'database_query';
const DATABASE_DESCRIBE_TOOL = 'database_describe';

function readSqlQuery(record: ToolResultRecord): SqlQueryInfo | null {
  if (record.tool !== DATABASE_QUERY_TOOL) return null;
  const ui = asRecord(record.ui_view);
  if (ui === null || typeof ui.sql !== 'string' || ui.sql.trim().length === 0) return null;
  return {
    sql: ui.sql,
    purpose: typeof ui.purpose === 'string' && ui.purpose.trim().length > 0 ? ui.purpose.trim() : null,
  };
}

export interface DeltaBlock {
  rows: Row[];
  floor: number | null;
  cells_withheld: number | null;
  note: string | null;
}

export interface CatalogSummary {
  metric_id: string;
  title: string;
  question_it_answers?: string;
  family?: string;
  grains?: string[];
  dimensions?: string[];
}

export type ToolPresentation =
  | { kind: 'envelopes'; envelopes: EnvelopeSlot[]; delta: DeltaBlock | null }
  | { kind: 'refusal'; refusal: { reason_code: string; message: string; metric_id: string | null; suggestion: string | null } }
  | { kind: 'catalog'; entries: CatalogSummary[] }
  | { kind: 'records'; entries: Record<string, unknown>[] }
  | { kind: 'describe'; entries: Record<string, unknown>[] }
  | { kind: 'acknowledged' }
  /** A `database_query` the database or the guard refused. The model reads the reason
   *  and retries; the attempt is how the agent worked, not the answer. */
  | { kind: 'attempt'; tool: string }
  | { kind: 'unknown'; tool: string };

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Is this an envelope? Structural, never `instanceof` and never a version
 * string: the surface must keep working when a metric adds a field.
 */
export function isEnvelope(value: unknown): value is MetricResultEnvelope {
  const record = asRecord(value);
  return (
    record !== null &&
    typeof record.metric_id === 'string' &&
    Array.isArray(record.columns) &&
    Array.isArray(record.rows)
  );
}

function readDelta(value: unknown): DeltaBlock | null {
  const record = asRecord(value);
  if (record === null || !Array.isArray(record.rows)) return null;
  return {
    rows: record.rows as Row[],
    floor: typeof record.floor === 'number' ? record.floor : null,
    cells_withheld: typeof record.cells_withheld === 'number' ? record.cells_withheld : null,
    note: typeof record.note === 'string' ? record.note : null,
  };
}

/**
 * Read one `ToolResultRecord` into something the screen can draw.
 *
 * `precision` selects which twin is read; when the requested twin is absent —
 * a tool that publishes only a `model_view` — the other is used, because a
 * blank panel would be a worse answer than the view that exists, and the
 * banner above the table always states which view is on screen.
 */
export function readToolPresentation(
  record: ToolResultRecord,
  precision: Precision,
): ToolPresentation {
  const wrapper = asRecord(record.model_view);

  // A disclosure refusal is a first-class ANSWER (spec 16.2 state 6).
  const refusal = wrapper === null ? null : asRecord(wrapper.refusal);
  if (refusal !== null) {
    return {
      kind: 'refusal',
      refusal: {
        reason_code: typeof refusal.reason_code === 'string' ? refusal.reason_code : 'refused',
        message: typeof refusal.message === 'string' ? refusal.message : '',
        metric_id: typeof refusal.metric_id === 'string' ? refusal.metric_id : null,
        suggestion: typeof refusal.suggestion === 'string' ? refusal.suggestion : null,
      },
    };
  }

  const modelInner = wrapper === null ? undefined : wrapper.model_view;
  const preferred = precision === 'ui' && record.ui_view !== undefined ? record.ui_view : modelInner;
  // The difference of the view ON SCREEN (2026-09-24): in "Full precision" a
  // comparison carries its own full-precision difference in `ui_view`, so the
  // operator no longer reads "No difference could be computed" under two sets
  // of numbers they can see. A record stored before that falls back to the
  // model's difference, as before.
  const delta = readDelta(asRecord(preferred)?.delta) ?? readDelta(asRecord(modelInner)?.delta);

  if (isEnvelope(preferred)) {
    const query = readSqlQuery(record);
    return {
      kind: 'envelopes',
      // The heading stays server-defined ("Database query"): `purpose` is written by the
      // model, so the card shows it as the agent's description, never as a title that
      // reads like a fact about the rows (security review of #3497, W1).
      envelopes: [{ label: null, envelope: preferred, query }],
      delta,
    };
  }

  // The schema lookup is how the agent found the answer, like metric_describe.
  if (record.tool === DATABASE_DESCRIBE_TOOL) {
    const entries = Array.isArray(preferred)
      ? preferred.filter((e): e is Record<string, unknown> => asRecord(e) !== null)
      : [];
    return { kind: 'describe', entries };
  }
  if (record.tool === DATABASE_QUERY_TOOL && wrapper !== null && asRecord(wrapper.error) !== null) {
    return { kind: 'attempt', tool: record.tool };
  }

  const pair = asRecord(preferred);
  if (pair !== null && isEnvelope(pair.window_a) && isEnvelope(pair.window_b)) {
    return {
      kind: 'envelopes',
      envelopes: [
        { label: 'Window A', envelope: pair.window_a },
        { label: 'Window B', envelope: pair.window_b },
      ],
      delta,
    };
  }

  if (Array.isArray(preferred)) {
    const entries = preferred.filter((e): e is Record<string, unknown> => asRecord(e) !== null);
    if (record.tool === 'catalog_search') {
      return { kind: 'catalog', entries: entries as unknown as CatalogSummary[] };
    }
    return { kind: 'records', entries };
  }

  if (pair !== null && pair.acknowledged === true) return { kind: 'acknowledged' };
  if (pair !== null && (record.tool === 'metric_describe' || record.tool === 'explore_describe')) {
    return { kind: 'describe', entries: [pair] };
  }

  return { kind: 'unknown', tool: record.tool };
}

/**
 * The envelopes a whole turn produced, in order — what the header pills, the
 * freshness stamp and the suppression notice are computed from.
 */
export function envelopesOfTurn(
  result: TurnResultPayload,
  precision: Precision,
): MetricResultEnvelope[] {
  const out: MetricResultEnvelope[] = [];
  for (const record of result.results) {
    const presentation = readToolPresentation(record, precision);
    if (presentation.kind === 'envelopes') {
      for (const slot of presentation.envelopes) out.push(slot.envelope);
    }
  }
  return out;
}

/**
 * Did this turn put ROWS on the operator's screen?
 *
 * 🔴 WHETHER THE OPERATOR SEES THEIR OWN TABLE MUST NOT DEPEND ON THE MODEL'S
 * LAST TOOL CALL. Seen at the UI on DEV, 2026-09-21: "how many matches were
 * created over the last 8 weeks" ran, the disclosure engine withheld the one
 * row from the model and served it to the operator — and the model then called
 * `clarify_or_decline` ("came back fully suppressed"). The screen showed the
 * decline panel ALONE and dropped the table the turn had already delivered.
 * The day before, the same question showed the row, because that day the model
 * happened to write a sentence instead.
 *
 * Read at FULL precision on purpose: the operator's view is the one that
 * decides whether there is something for the operator to look at. A result
 * withheld from the model in full has `rows: []` in the model view and would
 * answer "no" here — the exact case this exists for.
 */
export function turnServedOperatorRows(result: TurnResultPayload): boolean {
  return envelopesOfTurn(result, 'ui').some((envelope) => envelope.rows.length > 0);
}
